// ============================================================================
// EASI Design Tokens — Single Source of Truth
// Consolidated from theme-provider, easi-theme, easi-colors + reference repos
// ============================================================================

export const EASI_DESIGN = {
  // ── Colors ──────────────────────────────────────────────────────────────
  color: {
    // Brand
    emerald: "#10B981",
    emeraldLight: "#34D399",
    emeraldDark: "#059669",
    emeraldDeep: "#047857",
    emeraldDarkest: "#065F46",
    emeraldPale: "#D1FAE5",
    emeraldBg: "#F0FDF4",
    emeraldBorder: "#BBF7D0",

    blue: "#3B82F6",
    blueLight: "#60A5FA",
    blueDark: "#2563EB",
    blueDeep: "#1E40AF",
    bluePale: "#DBEAFE",
    blueBorder: "#BFDBFE",
    blueBg: "#EFF6FF",

    // Warm accent — replaced teal/purple with golden amber
    warmGreen: "#2D7D46",
    warmGreenLight: "#4A9B5F",
    warmGreenDark: "#1E6B35",

    gold: "#D4A855",
    goldDark: "#B8922E",

    // Legacy aliases (kept so existing components don't break)
    teal: "#2D7D46",
    tealLight: "#4A9B5F",
    tealDark: "#1E6B35",

    purple: "#2D7D46",
    purpleDark: "#1E6B35",

    // True purple (for IPA references, emphasis chips)
    violet: "#8B5CF6",
    violetDark: "#6D28D9",
    violetDeep: "#5B21B6",
    violetPale: "#EDE9FE",
    violetBg: "#F5F3FF",
    violetBorder: "#C4B5FD",

    amber: "#F59E0B",
    amberMid: "#D97706",
    amberDark: "#92400E",
    amberPale: "#FDE68A",
    amberBg: "#FFFBEB",
    amberLightBg: "#FEF3C7",

    // Additional green shades
    green600: "#16A34A",
    green800: "#166534",

    red: "#EF4444",
    redDark: "#DC2626",
    redDeep: "#991B1B",
    redDarkest: "#B91C1C",
    redBg: "#FEF2F2",
    redPale: "#FEE2E2",
    redBorder: "#FECACA",

    // Neutrals
    slate900: "#0F172A",
    slate800: "#1E293B",
    slate700: "#334155",
    slate600: "#475569",
    slate500: "#64748B",
    slate400: "#94A3B8",
    slate300: "#CBD5E1",
    slate200: "#E2E8F0",
    slate100: "#F1F5F9",
    slate50: "#F8FAFC",

    gray50: "#F9FAFB",
    gray100: "#F3F4F6",
    gray200: "#E5E7EB",
    gray300: "#D1D5DB",
    gray400: "#9CA3AF",
    gray500: "#6B7280",
    gray700: "#374151",

    white: "#FFFFFF",

    // Speech-analysis specific
    waveform: "#60A5FA",
    speaker1: "#60A5FA",
    speaker2: "#5EEAD4",
    processing: "#10B981",
  },

  // ── Gradients ───────────────────────────────────────────────────────────
  gradient: {
    brand: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    emerald: "linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%)",
    emeraldButton: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
    emeraldButtonHover: "linear-gradient(135deg, #059669 0%, #047857 100%)",
    blue: "linear-gradient(135deg, #3B82F6, #2563EB)",
    purple: "linear-gradient(135deg, #2D7D46, #1E6B35)",
    tealBlue: "linear-gradient(to right, #2D7D46, #10B981)",
    purpleTeal: "linear-gradient(135deg, #2D7D46, #10B981)",
    warmBg: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
    // Animated mesh for login
    mesh: "linear-gradient(135deg, #10B981 0%, #059669 25%, #2D7D46 50%, #10B981 75%, #059669 100%)",
    // Subtle page background
    pageBg: "linear-gradient(180deg, #F0FDF4 0%, #F8FAFC 40%, #F1F5F9 100%)",
  },

  // ── Shadows ─────────────────────────────────────────────────────────────
  shadow: {
    xs: "0 1px 2px 0 rgba(0,0,0,0.05)",
    sm: "0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px 0 rgba(0,0,0,0.06)",
    md: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)",
    lg: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
    xl: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    "2xl": "0 25px 50px -12px rgba(0,0,0,0.15)",
    emeraldGlow: "0 8px 24px rgba(16,185,129,0.2)",
    emeraldGlowStrong: "0 12px 28px rgba(16,185,129,0.3)",
    blueGlow: "0 8px 24px rgba(59,130,246,0.2)",
    blueGlowStrong: "0 12px 28px rgba(59,130,246,0.3)",
    purpleGlow: "0 8px 24px rgba(45,125,70,0.2)",
    cardHover: "0 12px 28px rgba(0,0,0,0.12)",
    cardElevated: "0 20px 40px rgba(0,0,0,0.08)",
    // Glass effect
    glass: "0 8px 32px rgba(0,0,0,0.08)",
  },

  // ── Easing Curves ───────────────────────────────────────────────────────
  easing: {
    smooth: "cubic-bezier(0.16, 1, 0.3, 1)",       // expo ease-out
    spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",    // elastic overshoot
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",       // material standard
    decel: "cubic-bezier(0.0, 0.0, 0.2, 1)",        // material decel
  },

  // ── Durations ───────────────────────────────────────────────────────────
  duration: {
    fast: 150,
    base: 200,
    smooth: 300,
    medium: 500,
    slow: 800,
  },

  // ── Pre-composed Transitions ────────────────────────────────────────────
  transition: {
    fast: "all 150ms ease",
    base: "all 200ms ease",
    smooth: "all 300ms cubic-bezier(0.16, 1, 0.3, 1)",
    medium: "all 500ms cubic-bezier(0.16, 1, 0.3, 1)",
    slow: "all 800ms cubic-bezier(0.16, 1, 0.3, 1)",
  },

  // ── Border Radius ───────────────────────────────────────────────────────
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    "2xl": 24,
    "3xl": 28,
    full: 9999,
  },

  // ── Spacing (4px base) ─────────────────────────────────────────────────
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    "2xl": 48,
    "3xl": 64,
  },

  // ── Typography ──────────────────────────────────────────────────────────
  typography: {
    fontFamily: '"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,system-ui,sans-serif',
    monoFamily: '"SF Mono","Fira Code","Fira Mono",Menlo,Consolas,monospace',
    weight: {
      regular: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },

  // ── Z-Index Layers ──────────────────────────────────────────────────────
  zIndex: {
    base: 0,
    dropdown: 100,
    sticky: 200,
    overlay: 300,
    modal: 400,
    popover: 500,
    toast: 600,
    fab: 1200,
  },

  // ── Glass Morphism Presets ──────────────────────────────────────────────
  glass: {
    light: {
      background: "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    dark: {
      background: "rgba(15, 23, 42, 0.7)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },
    subtle: {
      background: "rgba(255, 255, 255, 0.5)",
      backdropFilter: "blur(8px)",
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
  },
} as const

// ── Backward-Compat Alias ───────────────────────────────────────────────
// Components that import EASI_TOKENS continue to work
export const EASI_TOKENS = {
  color: EASI_DESIGN.color,
  gradient: EASI_DESIGN.gradient,
  shadow: EASI_DESIGN.shadow,
  transition: EASI_DESIGN.transition,
  easing: EASI_DESIGN.easing,
  radius: EASI_DESIGN.radius,
} as const

// ── easiColors compat export (used by easi-colors.tsx / loading animation) ─
export const easiColors = {
  primary: EASI_DESIGN.color.emerald,
  secondary: EASI_DESIGN.color.blue,
  accent: EASI_DESIGN.color.amber,
  light: EASI_DESIGN.color.slate50,
  medium: EASI_DESIGN.color.slate500,
  dark: EASI_DESIGN.color.slate800,
  background: EASI_DESIGN.color.blueBg,
  paper: EASI_DESIGN.color.white,
  success: EASI_DESIGN.color.emerald,
  warning: EASI_DESIGN.color.amber,
  error: EASI_DESIGN.color.red,
  info: EASI_DESIGN.color.blue,
  waveform: EASI_DESIGN.color.waveform,
  speaker1: EASI_DESIGN.color.speaker1,
  speaker2: EASI_DESIGN.color.speaker2,
  processing: EASI_DESIGN.color.processing,
} as const

export const EASI_GRADIENT = EASI_DESIGN.gradient.brand
